'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { ArrowRight } from 'lucide-react';
import { projectsData } from '@/lib/projects/data';

const categories = [
  { id: 'all', label: 'All Work' },
  { id: 'web', label: 'Web Design' },
  { id: 'ecom', label: 'E-Commerce' },
  { id: 'mobile', label: 'Mobile' },
];

export default function ProjectsGrid() {
  const [activeCategory, setActiveCategory] = useState('all');

  const filtered = activeCategory === 'all'
    ? projectsData
    : projectsData?.filter((p) => p?.categorySlug === activeCategory);

  return (
    <>
      {/* Filter tabs */}
      <div className="flex flex-wrap items-center justify-center gap-2 mb-10 overflow-x-auto pb-1">
        {categories?.map((cat) => (
          <button
            key={cat?.id}
            onClick={() => setActiveCategory(cat?.id)}
            className={`px-4 py-2 rounded-full text-sm font-semibold font-display transition-all duration-200 whitespace-nowrap ${
              activeCategory === cat?.id
                ? 'bg-primary text-white shadow-glow-primary'
                : 'glass-card text-muted-foreground hover:text-foreground border border-border/40'
            }`}
            aria-pressed={activeCategory === cat?.id}
          >
            {cat?.label}
          </button>
        ))}
      </div>
      {/* Projects grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {filtered?.map((project) => (
          <article
            key={project?.slug}
            className="group glass-card rounded-2xl overflow-hidden border border-border/40 hover:border-primary/30 card-hover flex flex-col"
          >
            <div className="relative h-52 overflow-hidden flex-shrink-0">
              <Image
                src={project?.heroImage}
                alt={project?.heroImageAlt}
                fill
                className="object-cover group-hover:scale-105 transition-transform duration-500"
                sizes="(max-width: 768px) 100vw, (max-width: 1280px) 50vw, 33vw"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background/70 to-transparent" />
              <span className="absolute top-3 left-3 text-xs font-mono-code px-2.5 py-1 rounded-full bg-background/70 border border-border/60 text-muted-foreground">
                {project?.category}
              </span>
            </div>
            <div className="p-5 flex flex-col flex-1">
              <h2 className="font-display font-bold text-base text-foreground mb-2 group-hover:text-accent transition-colors">
                {project?.title}
              </h2>
              <p className="text-sm text-muted-foreground leading-relaxed mb-4 flex-1 line-clamp-3">
                {project?.description}
              </p>
              <div className="flex flex-wrap gap-1.5 mb-4">
                {project?.technologies?.slice(0, 3)?.map((t) => (
                  <span key={t} className="text-[11px] font-mono-code px-2 py-0.5 bg-muted/60 rounded text-muted-foreground">
                    {t}
                  </span>
                ))}
              </div>
              <Link
                href={`/projects/${project?.slug}`}
                className="inline-flex items-center gap-1.5 text-sm font-semibold font-display text-muted-foreground group-hover:text-accent transition-colors duration-200"
                aria-label={`View case study for ${project?.title}`}
              >
                View Case Study <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform duration-200" />
              </Link>
            </div>
          </article>
        ))}
      </div>
      {filtered?.length === 0 && (
        <div className="text-center py-16">
          <p className="text-muted-foreground">No projects in this category yet.</p>
        </div>
      )}
    </>
  );
}
